import * as React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { addSession, getSession, useAppDispatch, useAppSelector } from '@wappsto/redux';
import { useRequest } from '@wappsto/blanket';
import Cookies from 'util/cookies';
import { isUUID } from 'util/helpers';
import Loader from 'components/helpers/Loader';
import { getWappKey } from 'configuration';

interface VerifierProps {
  Main: React.ElementType;
  Login: React.ElementType;
  Wrapper: React.ElementType;
}

const Verifier = React.memo(({ Main, Login, Wrapper }: VerifierProps) => {
  const dispatch = useAppDispatch();
  const session = useAppSelector(getSession);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const requestQuery = urlParams.get('request');
  const { request, send } = useRequest();

  useEffect(() => {
    if (
      location.search &&
      (location.search.startsWith('?X-session') || location.search.startsWith('?x-session'))
    ) {
      const xSession = location.search.substring(location.search.indexOf('=') + 1);
      if (isUUID(xSession)) {
        Cookies.create('sessionID', xSession, 30);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (!session && !request && !requestQuery) {
      const cachedSession = getWappKey('sessionID');
      if (cachedSession) {
        send({
          method: 'GET',
          url: `/session/${cachedSession}`,
          headers: {
            'x-session': cachedSession,
          },
        });
      }
    }
  }, [session, request, requestQuery, send]);

  useEffect(() => {
    if (request && request.status === 'success' && request.json) {
      dispatch(addSession(request.json));
    }
  }, [dispatch, request]);

  // check if 'session' exists, session validity is handled in InvalidSession component
  if (session) {
    return (
      <Wrapper>
        <Main/>
      </Wrapper>
    );
  }

  if (request) {
    if (request.status === 'pending' || (request.status === 'success' && request.json)) {
      return <Loader/>;
    }
  } else if (!requestQuery) {
    const cachedSession = getWappKey('sessionID');
    if (cachedSession) {
      return <Loader/>;
    }
  }

  return (
    <Wrapper>
      <Login requestQuery={requestQuery} location={location}/>
    </Wrapper>
  );
});

Verifier.displayName = 'SessionVerifier';

export default Verifier;
